import { memo } from 'react';
import { Path } from '../types';
import { Route, Grid3x3, Fence, Check, X, Triangle, TreePine, Pencil } from 'lucide-react';

interface DrawingToolbarProps {
  activeType: Path['type'] | null;
  onSelectType: (type: Path['type'] | null) => void;
  isDrawing: boolean;
  onFinish: () => void;
  onCancel: () => void;
}

export const DrawingToolbar = memo(function DrawingToolbar({ activeType, onSelectType, isDrawing, onFinish, onCancel }: DrawingToolbarProps) {
  const tools = [
    { type: 'course' as const, icon: Route, label: 'Tracé de course', color: 'bg-blue-600' },
    { type: 'net' as const, icon: Grid3x3, label: 'Filet', color: 'bg-orange-600' },
    { type: 'barrier' as const, icon: Fence, label: 'Barrière', color: 'bg-gray-600' },
    { type: 'v-board' as const, icon: Triangle, label: 'V-Board', color: 'bg-yellow-600' },
    { type: 'sapinettes' as const, icon: TreePine, label: 'Sapinettes', color: 'bg-green-700' },
    { type: 'custom' as const, icon: Pencil, label: 'Personnalisé', color: 'bg-purple-600' }
  ];

  return (
    <div className="w-full bg-white border border-gray-200 rounded-lg p-2">
      <div className="flex justify-between items-center mb-2">
        <p className="text-xs text-gray-600 flex-1 text-center">
          {isDrawing 
            ? 'Cliquez pour ajouter des points. Terminez ou annulez le tracé.'
            : 'Sélectionnez un type puis cliquez pour dessiner'}
        </p>
        {isDrawing && (
          <div className="flex gap-2 ml-4">
            <button
              type="button"
              onClick={onFinish}
              className="flex items-center gap-1 px-3 py-1 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-xs"
            >
              <Check className="w-3 h-3" />
              Terminer
            </button>
            <button
              type="button"
              onClick={onCancel}
              className="flex items-center gap-1 px-3 py-1 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-xs"
            >
              <X className="w-3 h-3" />
              Annuler
            </button>
          </div>
        )}
      </div>
      <div className="flex justify-center">
        <div className="grid grid-cols-6 gap-1">
          {tools.map((tool) => {
            const Icon = tool.icon;
            const isActive = activeType === tool.type;
            return (
              <button
                key={tool.type}
                type="button"
                onClick={() => onSelectType(isActive ? null : tool.type)}
                disabled={isDrawing && !isActive}
                className={`flex flex-col items-center gap-1 p-2 rounded-lg border-2 transition-all ${
                  isActive
                    ? 'border-blue-600 bg-blue-50 shadow-md'
                    : isDrawing
                    ? 'border-gray-200 bg-gray-100 opacity-50 cursor-not-allowed'
                    : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                }`}
              >
                <div className={`w-6 h-6 ${tool.color} rounded-full flex items-center justify-center`}>
                  <Icon className="w-4 h-4 text-white" />
                </div>
                <span className="text-[10px] text-center leading-tight">{tool.label}</span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
});